/**
 * Models Controller
 *
 * Provider-centric LLM management. Shows providers explicitly added
 * through the UI plus auto-discovered providers from the gateway config.
 */

import type {
  ModelsAdminProps,
  ModelEntry,
  ProviderEntry,
  ProviderFormEntry,
} from "../views/models-admin.js";

// --------------- Constants ---------------

const DISABLED_MODELS_KEY = "igloobot-disabled-models";
const MANAGED_PROVIDERS_KEY = "igloobot-managed-providers";

export const KNOWN_PROVIDERS: ReadonlyArray<{
  id: string;
  name: string;
  envVar: string;
  apiProtocol: string;
  baseUrl: string;
  description: string;
  local?: boolean;
  needsProviderConfig?: boolean;
}> = [
  {
    id: "anthropic",
    name: "Anthropic",
    envVar: "ANTHROPIC_API_KEY",
    apiProtocol: "anthropic-messages",
    baseUrl: "https://api.anthropic.com",
    description: "Claude models",
  },
  {
    id: "openai",
    name: "OpenAI",
    envVar: "OPENAI_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://api.openai.com/v1",
    description: "GPT & o-series",
  },
  {
    id: "google",
    name: "Google (Gemini)",
    envVar: "GEMINI_API_KEY",
    apiProtocol: "google-generative-ai",
    baseUrl: "https://generativelanguage.googleapis.com",
    description: "Gemini models",
  },
  {
    id: "openrouter",
    name: "OpenRouter",
    envVar: "OPENROUTER_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://openrouter.ai/api/v1",
    description: "Multi-provider gateway",
  },
  {
    id: "groq",
    name: "Groq",
    envVar: "GROQ_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://api.groq.com/openai/v1",
    description: "Fast inference",
  },
  {
    id: "xai",
    name: "xAI (Grok)",
    envVar: "XAI_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://api.x.ai/v1",
    description: "Grok models",
  },
  {
    id: "mistral",
    name: "Mistral",
    envVar: "MISTRAL_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://api.mistral.ai/v1",
    description: "Mistral models",
  },
  {
    id: "together",
    name: "Together AI",
    envVar: "TOGETHER_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://api.together.xyz/v1",
    description: "Open-source models",
  },
  {
    id: "cerebras",
    name: "Cerebras",
    envVar: "CEREBRAS_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://api.cerebras.ai/v1",
    description: "Fast inference",
  },
  {
    id: "huggingface",
    name: "HuggingFace",
    envVar: "HF_TOKEN",
    apiProtocol: "openai-completions",
    baseUrl: "https://api-inference.huggingface.co/v1/",
    description: "Serverless & custom endpoints",
    needsProviderConfig: true,
  },
  {
    id: "ollama",
    name: "Ollama",
    envVar: "OLLAMA_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "http://127.0.0.1:11434/v1",
    description: "Local open-source models",
    local: true,
  },
  {
    id: "lmstudio",
    name: "LM Studio",
    envVar: "LM_STUDIO_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "http://192.168.44.100:1234/v1",
    description: "Local model server",
    local: true,
    needsProviderConfig: true,
  },
  {
    id: "ark",
    name: "ByteDance Ark",
    envVar: "ARK_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://ark.ap-southeast.bytepluses.com/api/v3",
    description: "Doubao/Seed models, BytePlus ModelArk",
    // Ark model ids are account specific (ep-… endpoints or activated models),
    // so the form asks for them; discovery fills the list when Ark exposes it.
    needsProviderConfig: true,
  },
  {
    id: "bailian",
    name: "Alibaba Token Plan",
    envVar: "DASHSCOPE_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://token-plan.ap-southeast-1.maas.aliyuncs.com/compatible-mode/v1",
    description: "Qwen models (Bailian / Model Studio)",
    // Base URL differs per region / Token Plan vs DashScope.
    needsProviderConfig: true,
  },
  {
    id: "deepseek",
    name: "DeepSeek",
    envVar: "DEEPSEEK_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://api.deepseek.com/v1",
    description: "DeepSeek chat & reasoner",
  },
  {
    id: "fireworks",
    name: "Fireworks AI",
    envVar: "FIREWORKS_API_KEY",
    apiProtocol: "openai-completions",
    baseUrl: "https://api.fireworks.ai/inference/v1",
    description: "GLM, Kimi, DeepSeek models",
    needsProviderConfig: true,
  },
];

// --------------- RPC Types ---------------

type ConfigResult = {
  path: string;
  exists: boolean;
  raw: string | null;
  parsed: unknown;
  valid: boolean;
  config: unknown;
  hash?: string;
  issues: unknown[];
  warnings: unknown[];
  legacyIssues: unknown[];
};
type ModelsListResult = {
  models?: Array<{
    id: string;
    name: string;
    provider: string;
    contextWindow?: number;
    reasoning?: boolean;
  }>;
};
type PatchResult = { ok?: boolean; error?: string; hash?: string };

// Embedding RPC result types
export type EmbeddingStatusResult = {
  provider: string;
  model: string | null;
  baseUrl: string | null;
  healthy: boolean;
  error?: string;
};
type EmbeddingDiscoverResult = {
  models: Array<{ id: string; owned_by?: string }>;
  endpoint?: string;
};

// Docling RPC result types
export type DoclingStatusResult = {
  configured: boolean;
  url: string | null;
  healthy: boolean;
  version?: string | null;
  formats?: string[] | null;
  error?: string;
};

// --------------- State ---------------

export interface ModelsState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;
  modelsLoading: boolean;
  modelsError: string | null;
  modelCatalog: ModelEntry[];
  providers: ProviderEntry[];
  configHash: string | null;
  modelsSelectedProvider: string | null;
  modelsProviderForm: ProviderFormEntry | null;
  modelsProviderFormDirty: boolean;
  modelsDisabledIds: string[];
  modelsManagedProviderIds: string[];
  // Legacy fields kept for app.ts compat — unused in this controller
  defaultModel: string | null;
  fallbackModels: string[];
  modelsEditingDefaults: boolean;
  modelsDefaultModelDraft: string;
  modelsFallbacksDraft: string[];
  // Embedding state
  embeddingModel: string | null;
  embeddingDimension: number | null;
  embeddingStatus: EmbeddingStatusResult | null;
  embeddingAvailableModels: Array<{ id: string; owned_by?: string }>;
  embeddingLoading: boolean;
  embeddingSaving: boolean;
  // Docling state
  doclingUrl: string;
  doclingModel: string | null;
  doclingOcrLanguage: string;
  doclingStatus: DoclingStatusResult | null;
  doclingLoading: boolean;
  doclingSaving: boolean;
}

export const MODELS_STATE_DEFAULTS: Omit<ModelsState, "client" | "connected"> = {
  modelsLoading: false,
  modelsError: null,
  modelCatalog: [],
  providers: [],
  configHash: null,
  modelsSelectedProvider: null,
  modelsProviderForm: null,
  modelsProviderFormDirty: false,
  modelsDisabledIds: [],
  modelsManagedProviderIds: [],
  defaultModel: null,
  fallbackModels: [],
  modelsEditingDefaults: false,
  modelsDefaultModelDraft: "",
  modelsFallbacksDraft: [],
  embeddingModel: null,
  embeddingDimension: null,
  embeddingStatus: null,
  embeddingAvailableModels: [],
  embeddingLoading: false,
  embeddingSaving: false,
  doclingUrl: "http://localhost:8001",
  doclingModel: null,
  doclingOcrLanguage: "eng",
  doclingStatus: null,
  doclingLoading: false,
  doclingSaving: false,
};

// --------------- localStorage ---------------

function loadJson(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveJson(key: string, ids: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

// --------------- Data Loading ---------------

export async function loadModelsTab(
  state: ModelsState,
  opts?: { refresh?: boolean },
): Promise<void> {
  if (!state.client || !state.connected) return;
  state.modelsLoading = true;
  state.modelsError = null;

  try {
    const modelsParams: Record<string, unknown> = {};
    if (opts?.refresh) modelsParams.refresh = true;

    const [catalogRes, configRes] = await Promise.all([
      state.client.request<ModelsListResult>("models.list", modelsParams),
      state.client.request<ConfigResult>("config.get", {}),
    ]);

    state.modelCatalog = catalogRes.models ?? [];
    state.modelsDisabledIds = loadJson(DISABLED_MODELS_KEY);
    state.modelsManagedProviderIds = loadJson(MANAGED_PROVIDERS_KEY);

    state.configHash = configRes.hash ?? null;

    // Auto-detect providers explicitly configured in the config file
    const managed = new Set(state.modelsManagedProviderIds);
    let managedChanged = false;

    const cfg = configRes.config as Record<string, unknown> | null;
    const modelsSection = cfg?.models as Record<string, unknown> | null;
    const providersCfg = modelsSection?.providers as Record<string, unknown> | null;

    if (providersCfg) {
      for (const provId of Object.keys(providersCfg)) {
        if (!managed.has(provId)) {
          managed.add(provId);
          managedChanged = true;
        }
      }
    }

    // Clean up stale IDs
    const knownIds = new Set(KNOWN_PROVIDERS.map((kp) => kp.id));
    const configIds = new Set(providersCfg ? Object.keys(providersCfg) : []);
    for (const id of [...managed]) {
      if (!knownIds.has(id) && !configIds.has(id)) {
        const hasModels = state.modelCatalog.some((m) => m.provider === id);
        if (!hasModels) {
          managed.delete(id);
          managedChanged = true;
        }
      }
    }

    if (managedChanged) {
      state.modelsManagedProviderIds = [...managed];
      saveJson(MANAGED_PROVIDERS_KEY, state.modelsManagedProviderIds);
    }

    buildProviderList(state);

    // Also load embedding + docling config in parallel (fire-and-forget, non-blocking)
    void loadEmbeddingConfig(state);
    void loadDoclingConfig(state);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    state.modelsError = msg;
  } finally {
    state.modelsLoading = false;
  }
}

/**
 * Trigger auto-discovery on the gateway. This forces the gateway to
 * re-run model discovery (poll /v1/models endpoints for Fireworks,
 * OpenRouter, LM Studio; load built-in catalog for Anthropic) and
 * regenerate models.json before returning the catalog.
 */
export async function discoverModels(state: ModelsState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.modelsLoading = true;
  state.modelsError = null;
  try {
    // refresh: true forces the gateway to bypass cache and re-run discovery
    await loadModelsTab(state, { refresh: true });
  } catch (err) {
    state.modelsError = err instanceof Error ? err.message : String(err);
  } finally {
    state.modelsLoading = false;
  }
}

/** Build provider entries from managed + auto-discovered providers. */
function buildProviderList(state: ModelsState): void {
  const managedIds = state.modelsManagedProviderIds;

  const groups = new Map<string, ModelEntry[]>();
  for (const model of state.modelCatalog) {
    const list = groups.get(model.provider) ?? [];
    list.push(model);
    groups.set(model.provider, list);
  }

  // Include auto-discovered providers that have models in the catalog
  // but aren't yet in the managed list. This shows providers detected
  // by the gateway's auto-discovery system (e.g. Anthropic, Fireworks).
  const allProviderIds = new Set([...managedIds, ...groups.keys()]);

  const disabled = new Set(state.modelsDisabledIds);
  const providers: ProviderEntry[] = [];

  for (const id of allProviderIds) {
    const models = groups.get(id) ?? [];
    const known = KNOWN_PROVIDERS.find((kp) => kp.id === id);
    const enabledCount = models.filter((m) => !disabled.has(`${id}/${m.id}`)).length;

    providers.push({
      id,
      name: known?.name ?? id,
      envVar: known?.envVar ?? `${id.toUpperCase().replace(/-/g, "_")}_API_KEY`,
      connected: models.length > 0,
      modelCount: models.length,
      enabledCount,
      apiProtocol: known?.apiProtocol ?? "openai-completions",
      description: known?.description ?? "",
      local: known?.local ?? false,
    });
  }

  state.providers = providers;
}

// --------------- Provider Form ---------------

export function startAddProvider(state: ModelsState): void {
  state.modelsProviderForm = {
    providerId: "",
    name: "",
    apiKey: "",
    baseUrl: "",
    envVar: "",
    isNew: true,
    modelIds: "",
  };
  state.modelsProviderFormDirty = false;
  state.modelsSelectedProvider = null;
}

export function startEditProvider(state: ModelsState, id: string): void {
  const provider = state.providers.find((p) => p.id === id);
  const known = KNOWN_PROVIDERS.find((kp) => kp.id === id);
  state.modelsProviderForm = {
    providerId: id,
    name: provider?.name ?? known?.name ?? id,
    apiKey: "",
    baseUrl: known?.baseUrl ?? "",
    envVar: provider?.envVar ?? known?.envVar ?? "",
    isNew: false,
    modelIds: "",
  };
  state.modelsProviderFormDirty = false;
}

export function selectFormProviderType(state: ModelsState, providerId: string): void {
  if (!state.modelsProviderForm) return;
  if (providerId === "custom") {
    state.modelsProviderForm = {
      ...state.modelsProviderForm,
      providerId: "custom",
      name: "",
      envVar: "",
      baseUrl: "",
      modelIds: "",
    };
  } else {
    const known = KNOWN_PROVIDERS.find((kp) => kp.id === providerId);
    state.modelsProviderForm = {
      ...state.modelsProviderForm,
      providerId,
      name: known?.name ?? providerId,
      envVar: known?.envVar ?? "",
      baseUrl: known?.baseUrl ?? "",
      modelIds: "",
    };
  }
  state.modelsProviderFormDirty = Boolean(state.modelsProviderForm.apiKey);
}

export function updateFormField(state: ModelsState, field: string, value: string): void {
  if (!state.modelsProviderForm) return;
  (state.modelsProviderForm as Record<string, unknown>)[field] = value;
  state.modelsProviderForm = { ...state.modelsProviderForm };
  state.modelsProviderFormDirty = true;
}

export function cancelForm(state: ModelsState): void {
  state.modelsProviderForm = null;
  state.modelsProviderFormDirty = false;
}

export async function saveProvider(state: ModelsState): Promise<void> {
  if (!state.client || !state.connected || !state.modelsProviderForm) return;
  const form = state.modelsProviderForm;

  if (!form.providerId) {
    state.modelsError = "Select a provider type";
    return;
  }

  const known = KNOWN_PROVIDERS.find((kp) => kp.id === form.providerId);
  const envVar = form.envVar || known?.envVar;
  if (!envVar) {
    state.modelsError = "Environment variable name is required";
    return;
  }

  try {
    const patch: Record<string, unknown> = {};

    // Resolve the actual provider ID early
    const provId =
      form.providerId === "custom"
        ? form.name
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, "")
        : form.providerId;

    // Build provider config for providers that need it
    const needsConfig = form.providerId === "custom" || known?.needsProviderConfig || known?.local;
    if (needsConfig) {
      const providerPatch: Record<string, unknown> = {
        baseUrl: (form.baseUrl?.trim() || known?.baseUrl || "").replace(/\?$/, ""),
        api: known?.apiProtocol ?? "openai-completions",
      };

      // Local providers use apiKey in the provider config (not env vars)
      if (known?.local) {
        providerPatch.apiKey = form.apiKey?.trim() || provId;
      } else if (form.apiKey?.trim()) {
        providerPatch.apiKey = form.apiKey.trim();
      }

      // Parse model IDs from textarea
      const modelDefs = (form.modelIds ?? "")
        .split(/[\n,]/)
        .map((s: string) => s.trim())
        .filter(Boolean)
        .map((id: string) => ({
          id,
          name: id,
          reasoning: false,
          input: ["text"],
          cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
          contextWindow: 128000,
          maxTokens: 4096,
        }));

      // Always send models: the config schema requires the array on a provider
      // entry, and an empty one is harmless — the backend merges in whatever it
      // discovers or ships as that provider's catalog.
      providerPatch.models = modelDefs;

      patch.models = {
        providers: {
          [provId]: providerPatch,
        },
      };
    } else {
      // Cloud providers: the form only asks for the API key, but a provider
      // entry still needs baseUrl and models to pass the config schema — the
      // backend merges its own catalog into the empty list.
      if (form.apiKey?.trim()) {
        patch.models = {
          providers: {
            [provId]: {
              baseUrl: (form.baseUrl?.trim() || known?.baseUrl || "").replace(/\?$/, ""),
              api: known?.apiProtocol ?? "openai-completions",
              apiKey: form.apiKey.trim(),
              models: [],
            },
          },
        };
      }
    }

    if (Object.keys(patch).length === 0) {
      state.modelsError = "Enter an API key or base URL";
      return;
    }

    // Ensure we have a fresh baseHash for optimistic concurrency
    if (!state.configHash) {
      const snap = await state.client.request<ConfigResult>("config.get", {});
      state.configHash = snap.hash ?? null;
    }
    if (!state.configHash) {
      state.modelsError = "Could not read config hash — try refreshing";
      return;
    }

    const res = await state.client.request<PatchResult>("config.patch", {
      raw: JSON.stringify(patch),
      baseHash: state.configHash,
    });

    if (res.ok) {
      state.modelsProviderForm = null;
      state.modelsProviderFormDirty = false;
      state.modelsError = null;

      // Register as managed
      const managed = new Set(state.modelsManagedProviderIds);
      managed.add(provId);
      state.modelsManagedProviderIds = [...managed];
      saveJson(MANAGED_PROVIDERS_KEY, state.modelsManagedProviderIds);

      buildProviderList(state);
      state.modelsSelectedProvider = provId;

      // Gateway restarts after config.patch (SIGUSR1).
      // Invalidate our cached configHash so next operation re-fetches it.
      state.configHash = null;

      // Poll for gateway restart: retry loadModelsTab with increasing delays
      const delays = [2000, 3000, 5000];
      for (const delay of delays) {
        await new Promise((r) => setTimeout(r, delay));
        try {
          await loadModelsTab(state);
          if (state.modelCatalog.length > 0) break;
        } catch {
          // Gateway still restarting, retry
        }
      }
    } else {
      state.modelsError = res.error ?? "Failed to save provider configuration";
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    state.modelsError = msg;
  }
}

export async function disconnectProvider(state: ModelsState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  const provider = state.providers.find((p) => p.id === id);
  if (!provider) return;

  try {
    // Build a merge patch that removes the provider AND cleans up any
    // model references that point to it (agent model.primary, fallbacks,
    // documentModel). Without this, the config validation fails with
    // "invalid config" because the model refs become dangling.
    const patch: Record<string, unknown> = {
      models: { providers: { [id]: null } },
    };

    // Check if any agent model refs or plugin config refs point to this provider
    // If so, we need to clear them in the same patch
    const configSnap = await state.client.request<ConfigResult>("config.get", {});
    const cfg = configSnap.config as Record<string, unknown> | null;
    if (cfg) {
      const agentsSection = cfg.agents as Record<string, unknown> | null;
      const agentList = agentsSection?.list as Array<Record<string, unknown>> | undefined;
      const agentDefaults = agentsSection?.defaults as Record<string, unknown> | undefined;
      const prefix = `${id}/`;

      // Build agent patches
      const agentsPatch: Record<string, unknown> = {};

      // Check defaults.model
      if (agentDefaults) {
        const defModel = agentDefaults.model as Record<string, unknown> | undefined;
        if (defModel) {
          const defPatch: Record<string, unknown> = {};
          if (typeof defModel.primary === "string" && defModel.primary.startsWith(prefix)) {
            defPatch.primary = null;
          }
          if (Array.isArray(defModel.fallbacks)) {
            const filtered = defModel.fallbacks.filter(
              (f: unknown) => typeof f !== "string" || !f.startsWith(prefix),
            );
            if (filtered.length !== defModel.fallbacks.length) {
              defPatch.fallbacks = filtered;
            }
          }
          if (Object.keys(defPatch).length > 0) {
            agentsPatch.defaults = { model: defPatch };
          }
        }
      }

      // Check each agent in list
      if (agentList) {
        const listPatch: Array<Record<string, unknown>> = [];
        for (let i = 0; i < agentList.length; i++) {
          const agent = agentList[i];
          if (!agent) continue;
          const agentModel = agent.model;
          const agentPatch: Record<string, unknown> = {};

          if (typeof agentModel === "string" && agentModel.startsWith(prefix)) {
            agentPatch.model = null;
          } else if (agentModel && typeof agentModel === "object") {
            const m = agentModel as Record<string, unknown>;
            const mPatch: Record<string, unknown> = {};
            if (typeof m.primary === "string" && m.primary.startsWith(prefix)) {
              mPatch.primary = null;
            }
            if (Array.isArray(m.fallbacks)) {
              const filtered = m.fallbacks.filter(
                (f: unknown) => typeof f !== "string" || !f.startsWith(prefix),
              );
              if (filtered.length !== m.fallbacks.length) {
                mPatch.fallbacks = filtered;
              }
            }
            if (Object.keys(mPatch).length > 0) {
              agentPatch.model = mPatch;
            }
          }

          if (Object.keys(agentPatch).length > 0) {
            // Merge patch for arrays: we can't patch individual elements,
            // so we include the full index with the patch
            listPatch[i] = agentPatch;
          }
        }
        if (listPatch.length > 0) {
          agentsPatch.list = listPatch;
        }
      }

      // Check plugin config (documentModel, embeddingModel)
      const plugins = cfg.plugins as Record<string, unknown> | null;
      const entries = plugins?.entries as Record<string, Record<string, unknown>> | null;
      if (entries) {
        const coreConfig = entries["igloobot-core"]?.config as Record<string, unknown> | undefined;
        if (coreConfig) {
          const corePatch: Record<string, unknown> = {};
          if (typeof coreConfig.documentModel === "string" && coreConfig.documentModel.startsWith(prefix)) {
            corePatch.documentModel = null;
          }
          if (Object.keys(corePatch).length > 0) {
            patch.plugins = { entries: { "igloobot-core": { config: corePatch } } };
          }
        }
      }

      if (Object.keys(agentsPatch).length > 0) {
        patch.agents = agentsPatch;
      }
    }

    // Ensure we have a fresh baseHash for optimistic concurrency
    if (!state.configHash) {
      state.configHash = configSnap.hash ?? null;
    }
    if (!state.configHash) {
      state.modelsError = "Could not read config hash — try refreshing";
      return;
    }

    const res = await state.client.request<PatchResult>("config.patch", {
      raw: JSON.stringify(patch),
      baseHash: state.configHash,
    });

    if (res.ok) {
      state.modelsSelectedProvider = null;
      state.modelsError = null;
      state.configHash = null; // Invalidate — gateway will restart

      // Remove from managed list
      state.modelsManagedProviderIds = state.modelsManagedProviderIds.filter((pid) => pid !== id);
      saveJson(MANAGED_PROVIDERS_KEY, state.modelsManagedProviderIds);
      buildProviderList(state);

      // Wait for gateway restart then reload
      await new Promise((r) => setTimeout(r, 3000));
      try {
        await loadModelsTab(state);
      } catch {
        /* gateway still restarting */
      }
    } else {
      state.modelsError = res.error ?? "Failed to disconnect provider";
    }
  } catch (err) {
    state.modelsError = String(err);
  }
}

// --------------- Provider Selection ---------------

export function selectProvider(state: ModelsState, id: string | null): void {
  state.modelsSelectedProvider = id;
  state.modelsProviderForm = null;
  state.modelsProviderFormDirty = false;
}

// --------------- Model Toggles ---------------

export function toggleModel(state: ModelsState, fullId: string): void {
  const set = new Set(state.modelsDisabledIds);
  if (set.has(fullId)) {
    set.delete(fullId);
  } else {
    set.add(fullId);
  }
  state.modelsDisabledIds = [...set];
  saveJson(DISABLED_MODELS_KEY, state.modelsDisabledIds);
  buildProviderList(state);
}

export function enableAllModels(state: ModelsState): void {
  if (!state.modelsSelectedProvider) return;
  const models = state.modelCatalog.filter((m) => m.provider === state.modelsSelectedProvider);
  const set = new Set(state.modelsDisabledIds);
  for (const m of models) set.delete(`${m.provider}/${m.id}`);
  state.modelsDisabledIds = [...set];
  saveJson(DISABLED_MODELS_KEY, state.modelsDisabledIds);
  buildProviderList(state);
}

export function disableAllModels(state: ModelsState): void {
  if (!state.modelsSelectedProvider) return;
  const models = state.modelCatalog.filter((m) => m.provider === state.modelsSelectedProvider);
  const set = new Set(state.modelsDisabledIds);
  for (const m of models) set.add(`${m.provider}/${m.id}`);
  state.modelsDisabledIds = [...set];
  saveJson(DISABLED_MODELS_KEY, state.modelsDisabledIds);
  buildProviderList(state);
}

// --------------- Embedding ---------------

/** Load embedding status + available models + current config from the gateway */
export async function loadEmbeddingConfig(state: ModelsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.embeddingLoading = true;
  try {
    // Fire all three in parallel
    const [statusRes, discoverRes, configRes] = await Promise.all([
      state.client.request<EmbeddingStatusResult>("embedding.status", {}),
      state.client
        .request<EmbeddingDiscoverResult>("embedding.discover", {})
        .catch(() => ({ models: [] }) as EmbeddingDiscoverResult),
      state.client.request<ConfigResult>("config.get", {}),
    ]);

    state.embeddingStatus = statusRes;
    state.embeddingAvailableModels = discoverRes.models ?? [];

    // Read the current embeddingModel from plugin config
    const cfg = configRes.config as Record<string, unknown> | null;
    const plugins = cfg?.plugins as Record<string, unknown> | null;
    const entries = plugins?.entries as Record<string, Record<string, unknown>> | null;
    const igloobotCore = entries?.["igloobot-core"];
    const pluginConfig = igloobotCore?.config as Record<string, unknown> | null;
    state.embeddingModel = (pluginConfig?.embeddingModel as string) ?? null;
    state.embeddingDimension = (pluginConfig?.embeddingDimension as number) ?? null;
    state.configHash = configRes.hash ?? state.configHash;
  } catch (err) {
    state.modelsError = err instanceof Error ? err.message : String(err);
  } finally {
    state.embeddingLoading = false;
  }
}

/**
 * Save the selected embedding model (and optionally dimension) via embedding.configure.
 *
 * This bypasses config.patch entirely — embedding.configure writes the config file
 * directly and hot-swaps the provider in memory without triggering a gateway restart.
 */
export async function saveEmbeddingModel(
  state: ModelsState,
  model: string,
  dimension?: number,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.embeddingSaving = true;
  try {
    const configureParams: Record<string, unknown> = {
      model: model || null,
    };
    if (dimension != null) {
      configureParams.dimension = dimension;
    }

    // Single RPC: persists config to disk + hot-swaps provider (no restart)
    const result = await state.client.request<{
      ok: boolean;
      model: string | null;
      dimension: number | null;
      baseUrl: string | null;
    }>("embedding.configure", configureParams);

    if (result.ok) {
      state.embeddingModel = result.model;
      state.embeddingDimension = result.dimension;
    }

    // Reload status to reflect the new provider
    setTimeout(() => void loadEmbeddingConfig(state), 1000);
  } catch (err) {
    state.modelsError = err instanceof Error ? err.message : String(err);
  } finally {
    state.embeddingSaving = false;
  }
}

/** Refresh just the embedding model discovery list */
export async function refreshEmbeddingModels(state: ModelsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<EmbeddingDiscoverResult>("embedding.discover", {});
    state.embeddingAvailableModels = res.models ?? [];
  } catch {
    // silently ignore — the dropdown will be empty
  }
}

// --------------- Docling ---------------

/** Load Docling status + current config from the gateway */
export async function loadDoclingConfig(state: ModelsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.doclingLoading = true;
  try {
    const [statusRes, configRes] = await Promise.all([
      state.client.request<DoclingStatusResult>("docling.status", {}).catch(() => null),
      state.client.request<ConfigResult>("config.get", {}),
    ]);

    if (statusRes) {
      state.doclingStatus = statusRes;
    }

    // Read the current doclingUrl from plugin config
    const cfg = configRes.config as Record<string, unknown> | null;
    const plugins = cfg?.plugins as Record<string, unknown> | null;
    const entries = plugins?.entries as Record<string, Record<string, unknown>> | null;
    const igloobotCore = entries?.["igloobot-core"];
    const pluginConfig = igloobotCore?.config as Record<string, unknown> | null;
    state.doclingUrl = (pluginConfig?.doclingUrl as string) || "http://localhost:8001";
    state.doclingModel = (pluginConfig?.documentModel as string) ?? null;
    state.doclingOcrLanguage = (pluginConfig?.ocrLanguage as string) || "eng";
    state.configHash = configRes.hash ?? state.configHash;
  } catch (err) {
    state.modelsError = err instanceof Error ? err.message : String(err);
  } finally {
    state.doclingLoading = false;
  }
}

/**
 * Save Docling config (URL, model, OCR language) via docling.configure.
 *
 * Writes config directly and hot-swaps the DocumentProcessor.
 */
export async function saveDoclingConfig(
  state: ModelsState,
  url: string,
  ocrLanguage: string,
  model: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.doclingSaving = true;
  try {
    const result = await state.client.request<{
      ok: boolean;
      url: string | null;
      model: string | null;
      ocrLanguage: string | null;
    }>("docling.configure", {
      url: url || null,
      model: model || null,
      ocrLanguage: ocrLanguage || "eng",
    });

    if (result.ok) {
      state.doclingUrl = result.url || "http://localhost:8001";
      state.doclingModel = result.model;
      state.doclingOcrLanguage = result.ocrLanguage || "eng";
    }

    // Reload status to reflect the new config
    setTimeout(() => void loadDoclingConfig(state), 1000);
  } catch (err) {
    state.modelsError = err instanceof Error ? err.message : String(err);
  } finally {
    state.doclingSaving = false;
  }
}

// --------------- Props Builder ---------------

export function buildModelsProps(state: ModelsState): ModelsAdminProps {
  const disabled = new Set(state.modelsDisabledIds);
  const managedIds = new Set(state.modelsManagedProviderIds);

  const selectedProvider = state.modelsSelectedProvider
    ? (state.providers.find((p) => p.id === state.modelsSelectedProvider) ?? null)
    : null;

  const selectedProviderModels = state.modelsSelectedProvider
    ? state.modelCatalog
        .filter((m) => m.provider === state.modelsSelectedProvider)
        .map((m) => ({ ...m, enabled: !disabled.has(`${m.provider}/${m.id}`) }))
    : [];

  const addableProviders = KNOWN_PROVIDERS.filter((kp) => !managedIds.has(kp.id)).map((kp) => ({
    id: kp.id,
    name: kp.name,
    description: kp.description,
  }));

  return {
    loading: state.modelsLoading,
    error: state.modelsError,
    providers: state.providers,
    selectedProvider,
    selectedProviderModels,
    form: state.modelsProviderForm,
    formDirty: state.modelsProviderFormDirty,
    addableProviders,
    embedding: {
      model: state.embeddingModel,
      dimension: state.embeddingDimension,
      status: state.embeddingStatus,
      availableModels: state.embeddingAvailableModels,
      loading: state.embeddingLoading,
      saving: state.embeddingSaving,
      onSelectModel: (model: string) => {
        state.embeddingModel = model;
      },
      onSave: (model: string, dimension?: number) =>
        void saveEmbeddingModel(state, model, dimension),
      onRefreshModels: () => void refreshEmbeddingModels(state),
    },
    docling: {
      url: state.doclingUrl,
      ocrLanguage: state.doclingOcrLanguage,
      selectedModel: state.doclingModel,
      availableModels: state.modelCatalog,
      status: state.doclingStatus,
      loading: state.doclingLoading,
      saving: state.doclingSaving,
      onSelectModel: (model: string) => {
        state.doclingModel = model || null;
      },
      onSave: (url: string, ocrLanguage: string, model: string) =>
        void saveDoclingConfig(state, url, ocrLanguage, model),
      onRefresh: () => void loadDoclingConfig(state),
    },
    onRefresh: () => void loadModelsTab(state, { refresh: true }),
    onDiscover: () => void discoverModels(state),
    onSelectProvider: (id: string) => selectProvider(state, id),
    onStartAdd: () => startAddProvider(state),
    onStartEdit: (id: string) => startEditProvider(state, id),
    onDisconnect: (id: string) => void disconnectProvider(state, id),
    onFormChange: (field: string, value: string) => updateFormField(state, field, value),
    onFormTypeChange: (id: string) => selectFormProviderType(state, id),
    onSaveForm: () => void saveProvider(state),
    onCancelForm: () => cancelForm(state),
    onToggleModel: (fullId: string) => toggleModel(state, fullId),
    onEnableAll: () => enableAllModels(state),
    onDisableAll: () => disableAllModels(state),
  };
}
